/**
 * Privacy & Data Page
 *
 * Privacy settings including data export, cookie preferences,
 * communication preferences, and data retention info.
 * Uses updateUserSettings mutation for persisting preferences.
 */

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Label,
  Switch,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  useToast,
  Separator,
} from '@bloomcode/ui';
import { MetaTags } from '@bloomcode/shared-modules';
import { useUpdateUserSettingsMutation } from '@bloomcode/graphql';
import { Shield, Download, Cookie, Mail, Database, FileText } from '@bloomcode/icons';

export default function PrivacyPage() {
  const { toast } = useToast();
  const [exportRequested, setExportRequested] = useState(false);

  const [updateUserSettings] = useUpdateUserSettingsMutation();

  const [cookiePrefs, setCookiePrefs] = useState({
    analytics: true,
    marketing: false,
  });

  const [commPrefs, setCommPrefs] = useState({
    marketingEmails: false,
    productUpdates: true,
  });

  const handleExportRequest = () => {
    setExportRequested(true);
    toast({
      title: 'Export requested',
      description: 'Your data export will be prepared and sent to your email address.',
    });
  };

  const handleCookieSave = async () => {
    try {
      await updateUserSettings({
        variables: {
          input: {
            appSettings: JSON.stringify({
              cookies: cookiePrefs,
            }),
          },
        },
      });
      toast({
        title: 'Cookie preferences saved',
        description: 'Your cookie preferences have been updated.',
      });
    } catch (error) {
      toast({
        title: 'Failed to save',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCommSave = async () => {
    try {
      await updateUserSettings({
        variables: {
          input: {
            notificationPreferences: JSON.stringify({
              marketingEmails: commPrefs.marketingEmails,
              productUpdates: commPrefs.productUpdates,
            }),
          },
        },
      });
      toast({
        title: 'Communication preferences saved',
        description: 'Your communication preferences have been updated.',
      });
    } catch (error) {
      toast({
        title: 'Failed to save',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Privacy & Data - Bloomcode Account</title>
      </Helmet>
      <MetaTags
        title="Privacy & Data Settings"
        description="Manage your privacy and data preferences"
      />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Privacy & Data</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <h1 className="text-2xl font-bold">Privacy & Data</h1>
          <p className="text-muted-foreground mt-1">
            Manage your data, cookies, and communication preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Data Export */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Download className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Data Export</CardTitle>
              </div>
              <CardDescription>Request a copy of all your personal data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You can request a full export of your personal data. The export will be prepared and
                sent to your registered email address as a downloadable file. This may take up to 24
                hours to process.
              </p>
              <Button variant="outline" onClick={handleExportRequest} disabled={exportRequested}>
                <FileText className="h-4 w-4 mr-2" />
                {exportRequested ? 'Export Requested' : 'Request Data Export'}
              </Button>
            </CardContent>
          </Card>

          {/* Cookie Preferences */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Cookie Preferences</CardTitle>
              </div>
              <CardDescription>Control which cookies are used on your account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Functional Cookies</Label>
                  <p className="text-xs text-muted-foreground">
                    Required for the platform to work properly
                  </p>
                </div>
                <Switch checked disabled aria-label="Functional cookies (always on)" />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="analytics-cookies" className="text-sm font-medium">
                    Analytics Cookies
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Help us understand how you use the platform
                  </p>
                </div>
                <Switch
                  id="analytics-cookies"
                  checked={cookiePrefs.analytics}
                  onCheckedChange={(checked) =>
                    setCookiePrefs((prev) => ({
                      ...prev,
                      analytics: !!checked,
                    }))
                  }
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="marketing-cookies" className="text-sm font-medium">
                    Marketing Cookies
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Used to deliver relevant advertisements
                  </p>
                </div>
                <Switch
                  id="marketing-cookies"
                  checked={cookiePrefs.marketing}
                  onCheckedChange={(checked) =>
                    setCookiePrefs((prev) => ({
                      ...prev,
                      marketing: !!checked,
                    }))
                  }
                />
              </div>

              <Button size="sm" onClick={handleCookieSave}>
                Save Cookie Preferences
              </Button>
            </CardContent>
          </Card>

          {/* Communication Preferences */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Communication Preferences</CardTitle>
              </div>
              <CardDescription>Choose what emails you&apos;d like to receive</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="marketing-emails" className="text-sm font-medium">
                    Marketing Emails
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Promotions, offers, and company news
                  </p>
                </div>
                <Switch
                  id="marketing-emails"
                  checked={commPrefs.marketingEmails}
                  onCheckedChange={(checked) =>
                    setCommPrefs((prev) => ({
                      ...prev,
                      marketingEmails: !!checked,
                    }))
                  }
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="product-updates" className="text-sm font-medium">
                    Product Updates
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    New features, improvements, and release notes
                  </p>
                </div>
                <Switch
                  id="product-updates"
                  checked={commPrefs.productUpdates}
                  onCheckedChange={(checked) =>
                    setCommPrefs((prev) => ({
                      ...prev,
                      productUpdates: !!checked,
                    }))
                  }
                />
              </div>

              <Button size="sm" onClick={handleCommSave}>
                Save Communication Preferences
              </Button>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Data Retention</CardTitle>
              </div>
              <CardDescription>How we store and manage your data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>Your personal data is retained for as long as your account is active.</p>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>Login history and session data is retained for 90 days.</p>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>After account deletion, your data is permanently removed within 30 days.</p>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    For more details, see our{' '}
                    <a
                      href="/privacy-policy"
                      className="text-primary underline underline-offset-4 hover:text-primary/80"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
